import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, TextField, Button } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import UserNav from '../components/UserNav';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import config from '../config';

const API_BASE = config.API_BASE;

function Profile() {
  const user = useSelector(state => state.auth.user);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editName, setEditName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [error, setError] = useState('');
  const [shareMsg, setShareMsg] = useState('');
  const history = useHistory();

  // Debug logging
  console.log('Profile.jsx debug:', { user, attempts, loading, error });

  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin') {
      history.replace('/admin/dashboard');
      return;
    }
    if (user && user.email) {
      fetch(`${API_BASE}/results/${user.email}`)
        .then(res => res.json())
        .then(data => {
          setAttempts(data);
          setLoading(false);
        })
        .catch(() => {
          setError('Failed to fetch test attempts.');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [user, history]);

  // Count attempts per test type (robust to variations in testType)
  const attemptCounts = {
    learningStrategy: attempts.filter(a => a.testType.toLowerCase().includes('learningstrategy')).length,
    media: attempts.filter(a => a.testType.toLowerCase().includes('media')).length,
    interactivity: attempts.filter(a => a.testType.toLowerCase().includes('interactivity')).length,
  };

  const handleSaveName = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${API_BASE}/user/update-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, name: editName })
      });
      const data = await res.json();
      if (res.ok) {
        setSaveMsg('Name updated!');
      } else {
        setSaveMsg(data.error || 'Failed to update name');
      }
    } catch {
      setSaveMsg('Failed to update name');
    }
    setSaving(false);
  };

  const handleUpdatePassword = async () => {
    setPwMsg('');
    if (!pw || !pw2) return setPwMsg('Please enter and confirm your new password.');
    if (pw !== pw2) return setPwMsg('Passwords do not match.');
    try {
      const res = await fetch(`${API_BASE}/user/update-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, password: pw })
      });
      const data = await res.json();
      if (res.ok) setPwMsg('Password updated!');
      else setPwMsg(data.error || 'Failed to update password');
    } catch {
      setPwMsg('Failed to update password');
    }
  };

  const handleShareResults = () => {
    const shareText = `Test Results for ${user.name || user.email}:\n` +
      attempts.map(a => `${a.testType}: ${a.result} (${a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''})`).join('\n');
    navigator.clipboard.writeText(shareText).then(() => setShareMsg('Results copied!'), () => setShareMsg('Failed to copy.'));
  };

  let content = null;
  if (error) {
    content = <Typography color="error">{error}</Typography>;
  } else if (!user) {
    content = <Typography color="error">Not logged in.</Typography>;
  } else {
    try {
      content = (
        <>
          <Typography variant="h6">Name:</Typography>
          <TextField
            value={editName}
            onChange={e => setEditName(e.target.value)}
            variant="outlined"
            size="small"
            style={{ marginBottom: 8, maxWidth: 320 }}
          />
          <Button onClick={handleSaveName} variant="contained" color="primary" size="small" disabled={saving} style={{ marginLeft: 8 }}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          {saveMsg && <Typography style={{ marginTop: 8 }} color={saveMsg === 'Name updated!' ? 'primary' : 'error'}>{saveMsg}</Typography>}
          <Typography variant="h6">Email: {user.email}</Typography>
          <Typography variant="h6" style={{marginTop: 16}}>Test Attempts</Typography>
          {loading ? <CircularProgress /> : (
            attempts.length === 0 ? (
              <Typography>No test attempts found.</Typography>
            ) : (
              <TableContainer component={Paper} style={{marginTop: 8}}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Test Type</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attempts.map((a, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{a.testType}</TableCell>
                        <TableCell>{a.result}</TableCell>
                        <TableCell>{a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
          <Typography variant="h6" style={{marginTop: 24}}>Attempt Counts</Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
            <TableContainer component={Paper} style={{ maxWidth: 480, marginTop: 8, marginBottom: 24 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Test Type</TableCell>
                    <TableCell align="center">Attempts</TableCell>
                    <TableCell align="center">Last Attempt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Learning Strategy Test</TableCell>
                    <TableCell align="center">{attemptCounts.learningStrategy}</TableCell>
                    <TableCell align="center">{attemptCounts.learningStrategy > 0 ? (attempts.filter(a => a.testType.toLowerCase().includes('learningstrategy')).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.createdAt ? new Date(attempts.filter(a => a.testType.toLowerCase().includes('learningstrategy')).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0].createdAt).toLocaleDateString() : '') : ''}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Media Test</TableCell>
                    <TableCell align="center">{attemptCounts.media}</TableCell>
                    <TableCell align="center">{attemptCounts.media > 0 ? (attempts.filter(a => a.testType.toLowerCase().includes('media')).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.createdAt ? new Date(attempts.filter(a => a.testType.toLowerCase().includes('media')).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0].createdAt).toLocaleDateString() : '') : ''}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Interactivity Test</TableCell>
                    <TableCell align="center">{attemptCounts.interactivity}</TableCell>
                    <TableCell align="center">{attemptCounts.interactivity > 0 ? (attempts.filter(a => a.testType.toLowerCase().includes('interactivity')).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.createdAt ? new Date(attempts.filter(a => a.testType.toLowerCase().includes('interactivity')).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0].createdAt).toLocaleDateString() : '') : ''}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* Chart fallback */}
            {(() => {
              try {
                return (
                  <ResponsiveContainer width={260} height={180}>
                    <BarChart data={[
                      { name: 'Learning', Attempts: attemptCounts.learningStrategy },
                      { name: 'Media', Attempts: attemptCounts.media },
                      { name: 'Interactivity', Attempts: attemptCounts.interactivity },
                    ]} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="Attempts" fill="#1A237E" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                );
              } catch (e) {
                return <Typography color="error">Chart failed to render: {e.message}</Typography>;
              }
            })()}
          </div>
          {/* Password update */}
          <Typography variant="h6" style={{marginTop: 32}}>Update Password</Typography>
          <TextField
            type="password"
            label="New Password"
            value={pw}
            onChange={e => setPw(e.target.value)}
            variant="outlined"
            size="small"
            style={{ marginBottom: 8, maxWidth: 320 }}
          />
          <TextField
            type="password"
            label="Confirm Password"
            value={pw2}
            onChange={e => setPw2(e.target.value)}
            variant="outlined"
            size="small"
            style={{ marginBottom: 8, maxWidth: 320, marginLeft: 8 }}
          />
          <Button onClick={handleUpdatePassword} variant="contained" color="primary" size="small" style={{ marginLeft: 8 }}>
            Update Password
          </Button>
          {pwMsg && <Typography style={{ marginTop: 8 }} color={pwMsg === 'Password updated!' ? 'primary' : 'error'}>{pwMsg}</Typography>}
          {/* Share results */}
          <Typography variant="h6" style={{marginTop: 32}}>Share Your Results</Typography>
          <Button onClick={handleShareResults} variant="outlined" color="primary" size="small">Copy Results to Clipboard</Button>
          {shareMsg && <Typography style={{ marginTop: 8 }} color={shareMsg === 'Results copied!' ? 'primary' : 'error'}>{shareMsg}</Typography>}
        </>
      );
    } catch (e) {
      content = <Typography color="error">Profile failed to render: {e.message}</Typography>;
    }
  }

  return (
    <Container>
      <UserNav />
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Typography variant="h4" gutterBottom style={{ flex: 1 }}>Profile</Typography>
      </div>
      {content}
    </Container>
  );
}

export default Profile; 